import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, ArrowLeft, Mail, Lock, User, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.email.toLowerCase() !== formData.confirmEmail.toLowerCase()) {
      toast.error("Os e-mails não coincidem! Dá uma olhadinha ali...");
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("As senhas não coincidem!");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres!");
      return;
    }

   setIsLoading(true);
    
    // Tratamento: remove espaços vazios e padroniza e-mail para minúsculo
    const cleanEmail = formData.email.trim().toLowerCase();
    const cleanName = formData.name.trim();

    const result = await register(cleanName, cleanEmail, formData.password);
    
    setIsLoading(false);
    
    if (result.success) {
      toast.success("Conta criada! Agora só falta verificar seu e-mail. 📧");
      navigate("/verify-email");
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-background px-6 py-8">
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
      >
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Voltar</span>
        </Link>
      </motion.div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-8 mb-8"
      >
        <h1 className="text-3xl font-extrabold tracking-tight text-foreground mb-2">
          Criar sua conta
        </h1>
        <p className="text-muted-foreground">
          Comece a organizar suas finanças agora
        </p>
      </motion.div>

      {/* Form */}
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name" className="font-medium">
            Seu nome
          </Label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="name"
              type="text"
              placeholder="Como podemos te chamar?"
              data-testid="register-name"
              className="input-modern pl-12"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email" className="font-medium">
            E-mail
          </Label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              data-testid="register-email"
              className="input-modern pl-12"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
        </div>

        {/* Confirm Email */}
        <div className="space-y-2">
          <Label htmlFor="confirmEmail" className="font-medium">
            Só para garantir: confirme seu e-mail.
          </Label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="confirmEmail"
              type="email"
              placeholder="seu@email.com de novo"
              className="input-modern pl-12"
              value={formData.confirmEmail}
              onChange={(e) => setFormData({ ...formData, confirmEmail: e.target.value })}
              required
            />
          </div>
        </div>


        {/* Password */}
        <div className="space-y-2">
          <Label htmlFor="password" className="font-medium">
            Senha
          </Label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Mínimo 6 caracteres"
              data-testid="register-password"
              className="input-modern pl-12 pr-12"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="toggle-password"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="font-medium">
            Confirmar senha
          </Label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              placeholder="Digite a senha novamente"
              data-testid="register-confirm-password"
              className="input-modern pl-12"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
            />
          </div>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          className="w-full h-14 rounded-full font-bold text-lg btn-primary"
          disabled={isLoading}
          data-testid="register-submit"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Criando sua conta...
            </>
          ) : (
            "Criar conta"
          )}
        </Button>
        
      </motion.form>

      {/* Terms and Security Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-6 space-y-4 px-4"
      >
        <p className="text-center text-[11px] text-muted-foreground leading-relaxed">
          Ao criar sua conta, você concorda com nossos{" "}
          <Link to="/legal" className="text-primary font-bold underline">
            Termos de Uso
          </Link>{" "}
          e nossa{" "}
          <Link to="/legal" className="text-primary font-bold underline">
            Política de Privacidade
          </Link>.
        </p>

        {/* Selo de Confiança - Essencial para App de Finanças */}
        <div className="flex justify-center items-center gap-2 py-2 px-4 bg-secondary/40 rounded-full mx-auto w-fit border border-border/50">
          <Lock className="w-3 h-3 text-primary" />
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
            Dados Financeiros Criptografados
          </span>
        </div>
      </motion.div>

      {/* Login Link */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-center mt-6 text-muted-foreground"
      >
        Já tem conta?{" "}
        <Link 
          to="/login" 
          className="text-primary font-bold hover:underline"
          data-testid="login-link"
        >
          Entrar
        </Link>
      </motion.p>
    </div>
  );
}
